import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import CtaSection from './components/CtaSection';
import SeoContent from './components/SeoContent';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { buildOrganizationJsonLd, buildWebSiteJsonLd } from '@/lib/seo';
import { Link } from 'react-router-dom';

const pillarArticles = [
  {
    path: '/basetao-spreadsheet-guide',
    title: 'Complete BaseTao Spreadsheet Guide 2026',
    desc: 'From beginner setup to advanced mastery — the definitive resource covering every feature, strategy, and best practice.',
    icon: 'ri-book-open-line',
    readTime: '18 min',
  },
  {
    path: '/basetao-spreadsheet-tutorial',
    title: 'Step-by-Step Tutorial for Beginners',
    desc: 'Hands-on training with practical examples to master product tracking, price comparison, and order management.',
    icon: 'ri-graduation-cap-line',
    readTime: '14 min',
  },
  {
    path: '/basetao-spreadsheet-review',
    title: 'Honest BaseTao Review 2026',
    desc: 'Unbiased feature-by-feature analysis, pricing evaluation, and real user experience insights.',
    icon: 'ri-star-line',
    readTime: '16 min',
  },
  {
    path: '/basetao-spreadsheet-template',
    title: 'Ready-to-Use Templates Collection',
    desc: 'Professionally designed spreadsheet templates for product tracking, cost analysis, and inventory management.',
    icon: 'ri-file-copy-line',
    readTime: '12 min',
  },
  {
    path: '/basetao-spreadsheet-formulas',
    title: 'Essential Formulas & Functions Guide',
    desc: 'Master over 200 formulas for cost calculation, price monitoring, shipping optimization, and automation.',
    icon: 'ri-function-line',
    readTime: '15 min',
  },
  {
    path: '/basetao-spreadsheet-automation',
    title: 'Workflow Automation Strategies',
    desc: 'Configure trigger-action rules to eliminate repetitive tasks and streamline your sourcing operations.',
    icon: 'ri-settings-3-line',
    readTime: '12 min',
  },
  {
    path: '/basetao-shipping-cost',
    title: 'International Shipping Cost Guide',
    desc: 'Complete breakdown of shipping methods, rate factors, consolidation benefits, and money-saving strategies.',
    icon: 'ri-ship-line',
    readTime: '13 min',
  },
  {
    path: '/basetao-coupon',
    title: 'Coupon Codes & Discount Guide',
    desc: 'Find and maximize discounts through seasonal promotions, bulk incentives, and automated coupon tracking.',
    icon: 'ri-coupon-line',
    readTime: '10 min',
  },
  {
    path: '/basetao-warehouse',
    title: 'Warehouse Storage & Consolidation',
    desc: 'Understand storage timelines, fee structures, and smart consolidation strategies for maximum savings.',
    icon: 'ri-store-line',
    readTime: '11 min',
  },
  {
    path: '/is-basetao-legit',
    title: 'Is BaseTao Legit? Safety Review',
    desc: 'Thorough investigation of security practices, data privacy, and platform trustworthiness.',
    icon: 'ri-shield-check-line',
    readTime: '14 min',
  },
  {
    path: '/basetao-spreadsheet-beginner',
    title: 'Absolute Beginner Getting Started',
    desc: 'Zero experience required — walkthrough from account creation to placing your first order with confidence.',
    icon: 'ri-user-smile-line',
    readTime: '12 min',
  },
];

export default function Home() {
  return (
    <>
      <JsonLd data={buildOrganizationJsonLd()} />
      <JsonLd data={buildWebSiteJsonLd()} />
      <Navbar />
      <main>
        <HeroSection />
        <CategoryGrid />
        <CtaSection />

        {/* Pillar Articles Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                In-Depth Learning Resources
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                Complete BaseTao Spreadsheet Knowledge Base
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                Explore our library of expert-written guides covering every aspect of BaseTao Spreadsheet. From absolute beginner tutorials to advanced optimization strategies — everything you need in one place.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {pillarArticles.map((article) => (
                <Link
                  key={article.path}
                  to={article.path}
                  className="group flex flex-col p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-200 cursor-pointer"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-200 transition-colors flex-shrink-0">
                      <i className={`${article.icon} text-lg text-primary-600`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors leading-snug mb-1">
                        {article.title}
                      </h3>
                      <span className="text-xs text-foreground-500">{article.readTime} read</span>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    {article.desc}
                  </p>
                  <div className="mt-4 flex items-center gap-1.5 text-xs text-primary-600 group-hover:text-primary-700 transition-colors">
                    <span>Read Article</span>
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <SeoContent />
      </main>
      <Footer />
    </>
  );
}